import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
    Wallet, 
    TrendingUp, 
    DollarSign, 
    PieChart,
    ArrowUpRight,
    ExternalLink,
    Shield,
    Clock,
    Globe
} from 'lucide-react';
import { CONTRACT_ADDRESS_V2, SUPPORTED_CHAINS } from '../lib/contract';

const TREASURY_ADDRESS = '0xAf122740bD739B49d980a48Dfb6E0141cCE79338';
const CONTRACT_ADDRESS = CONTRACT_ADDRESS_V2;
const PLATFORM_FEE_PERCENT = 3;

export function TreasuryPage() {
    const { address } = useWeb3();
    const [treasuryData, setTreasuryData] = useState(null);
    const [loading, setLoading] = useState(true);

    const isOwner = address?.toLowerCase() === TREASURY_ADDRESS.toLowerCase();

    useEffect(() => {
        fetchTreasuryData();
        const interval = setInterval(fetchTreasuryData, 30000); // Refresh every 30s
        return () => clearInterval(interval);
    }, []);

    const fetchTreasuryData = async () => {
        try {
            // Fetch treasury balance from Base Mainnet using public RPC
            let balanceEth = '0';
            try {
                const balanceResponse = await fetch('https://mainnet.base.org', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        jsonrpc: '2.0',
                        id: 1,
                        method: 'eth_getBalance',
                        params: [TREASURY_ADDRESS, 'latest']
                    })
                });
                const balanceData = await balanceResponse.json();
                if (balanceData.result) {
                    balanceEth = (parseInt(balanceData.result, 16) / 1e18).toFixed(6);
                }
            } catch (e) {
                console.error('Failed to fetch balance:', e);
            }

            // Fetch contract stats
            const statsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats`);
            const stats = await statsResponse.json();

            setTreasuryData({
                balance: balanceEth,
                totalFees: stats.total_fees || 0,
                totalVolume: stats.total_volume || 0,
                totalBets: stats.total_bets || 0,
                resolvedBets: stats.resolved_bets || 0,
            });
        } catch (error) {
            console.error('Failed to fetch treasury data:', error);
            setTreasuryData({
                balance: '0',
                totalFees: 0,
                totalVolume: 0,
                totalBets: 0,
                resolvedBets: 0,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="treasury-page">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-[#FFD700]/20 flex items-center justify-center">
                        <Wallet className="w-6 h-6 text-[#FFD700]" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-white">
                            <span className="text-[#FFD700]">Treasury</span> Dashboard
                        </h1>
                        <p className="text-white/50">Platform revenue and fee collection</p>
                    </div>
                </div>
                {isOwner && (
                    <Badge className="bg-[#00FF66]/20 text-[#00FF66] border-[#00FF66]/30">
                        <Shield className="w-3 h-3 mr-1" />
                        Owner View
                    </Badge>
                )}
            </div>

            {/* Treasury Address */}
            <Card className="bg-white/[0.02] border-white/5 mb-6">
                <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <DollarSign className="w-5 h-5 text-[#FFD700]" />
                            <div>
                                <div className="text-sm text-white/50">Treasury Address</div>
                                <div className="font-mono text-white">{TREASURY_ADDRESS}</div>
                            </div>
                        </div>
                        <a 
                            href={`https://basescan.org/address/${TREASURY_ADDRESS}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-[#00FF66] hover:underline"
                        >
                            View on BaseScan
                            <ExternalLink className="w-4 h-4" />
                        </a>
                    </div>
                </CardContent>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Treasury Balance */}
                <Card className="bg-gradient-to-br from-[#FFD700]/20 to-[#FFD700]/5 border-[#FFD700]/20">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-white/60 flex items-center gap-2">
                            <Wallet className="w-4 h-4 text-[#FFD700]" />
                            Treasury Balance
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Skeleton className="h-10 w-32 bg-white/10" />
                        ) : (
                            <div className="font-mono text-3xl font-bold text-[#FFD700]">
                                {treasuryData?.balance} ETH
                            </div>
                        )}
                        <p className="text-xs text-white/40 mt-1">Real-time balance</p>
                    </CardContent>
                </Card>

                {/* Total Fees Collected */}
                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-white/60 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-[#00FF66]" />
                            Fees Collected
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Skeleton className="h-10 w-32 bg-white/10" />
                        ) : (
                            <div className="font-mono text-3xl font-bold text-[#00FF66]">
                                {treasuryData?.totalFees?.toFixed(4)} ETH
                            </div>
                        )}
                        <p className="text-xs text-white/40 mt-1">{PLATFORM_FEE_PERCENT}% platform fee</p>
                    </CardContent>
                </Card>

                {/* Total Volume */}
                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-white/60 flex items-center gap-2">
                            <PieChart className="w-4 h-4 text-[#9B6FE8]" />
                            Total Volume
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Skeleton className="h-10 w-32 bg-white/10" />
                        ) : (
                            <div className="font-mono text-3xl font-bold text-white">
                                {treasuryData?.totalVolume?.toFixed(4)} ETH
                            </div>
                        )}
                        <p className="text-xs text-white/40 mt-1">All-time betting volume</p>
                    </CardContent>
                </Card>

                {/* Resolved Bets */}
                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-white/60 flex items-center gap-2">
                            <Clock className="w-4 h-4 text-white/60" />
                            Resolved Bets
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <Skeleton className="h-10 w-32 bg-white/10" />
                        ) : (
                            <div className="font-mono text-3xl font-bold text-white">
                                {treasuryData?.resolvedBets} / {treasuryData?.totalBets}
                            </div>
                        )}
                        <p className="text-xs text-white/40 mt-1">Fee-generating bets</p>
                    </CardContent>
                </Card>
            </div>

            {/* Contract Info */}
            <Card className="bg-white/[0.02] border-white/5">
                <CardHeader>
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                        <Shield className="w-5 h-5 text-[#00FF66]" />
                        Smart Contract V2
                    </CardTitle>
                    <CardDescription className="text-white/50">
                        Bet4PeerV2 deployed on Base, Arbitrum, and Polygon with UMA Oracle
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 rounded-lg bg-white/[0.02] border border-white/5">
                            <div>
                                <div className="text-sm text-white/50">Contract Address (Same on all chains)</div>
                                <div className="font-mono text-white">{CONTRACT_ADDRESS}</div>
                            </div>
                            <div className="flex gap-2">
                                {Object.entries(SUPPORTED_CHAINS).map(([chainId, chain]) => (
                                    <a 
                                        key={chainId}
                                        href={`${chain.explorer}/address/${CONTRACT_ADDRESS}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 px-2 py-1 rounded bg-white/5 text-xs text-[#00FF66] hover:bg-white/10"
                                        title={`View on ${chain.name}`}
                                    >
                                        {chain.name}
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                                <div className="text-xs text-white/40">Networks</div>
                                <div className="text-sm font-semibold text-white flex items-center gap-1">
                                    <Globe className="w-3 h-3 text-[#00FF66]" />
                                    3 Chains
                                </div>
                            </div>
                            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                                <div className="text-xs text-white/40">Version</div>
                                <div className="text-sm font-semibold text-white">V2 + UMA</div>
                            </div>
                            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                                <div className="text-xs text-white/40">Platform Fee</div>
                                <div className="text-sm font-semibold text-[#FFD700]">{PLATFORM_FEE_PERCENT}%</div>
                            </div>
                            <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                                <div className="text-xs text-white/40">Status</div>
                                <div className="text-sm font-semibold text-[#00FF66]">Active</div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
